'use client';

import React, { useRef } from 'react';
import { Box } from '@shared/ui';
import omit from 'lodash-es/omit';
import { type LayoutNodeStyleMap } from 'shop-ui/types';
import {
    ELEMENT_TYPE,
    type FEATURE_FLAG_KEYS,
    LAYOUT_SECTION_NAME,
} from 'shop-ui/constants';
import { ShopUIWrapper, MotionableBox } from 'shop-ui/component';
import { Container } from 'shop-ui/section';
import { usePreviewUIStyle } from '../usePreviewUIStyle';

export interface EnhanceUIPropTypes {
    id: string;
    layoutId: string;
    type: ELEMENT_TYPE;
    feature: FEATURE_FLAG_KEYS[];
    style: LayoutNodeStyleMap[string];
    content: string;
    children: React.ReactNode;
    enable?: boolean;
    sectionName: LAYOUT_SECTION_NAME;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withUIContext(WrappedShopUI: React.FC<any>) {
    function UINode(props: EnhanceUIPropTypes) {
        const { id, type, enable, feature, style, content } = props;

        const ref = useRef<HTMLDivElement>(null);

        const context = usePreviewUIStyle({
            id,
            type,
            style,
            content,
            feature,
        });

        return (
            <Box
                data-shop-element-id={id}
                id={id}
                as={type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL ? Container : ShopUIWrapper}
                h={
                    type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL ||
                    type === ELEMENT_TYPE.GRID_SECONDARY_LEVEL ||
                    type === ELEMENT_TYPE.GRID_SECONDARY_LEVEL_INNER
                        ? 'full'
                        : 'fit-content'
                }
                {...(type !== ELEMENT_TYPE.GRID_PRIMARY_LEVEL &&
                    type !== ELEMENT_TYPE.GRID_SECONDARY_LEVEL &&
                    type !== ELEMENT_TYPE.GRID_SECONDARY_LEVEL_INNER && {
                        style: context.containerStyle,
                    })}
                {...(context.containerExtraProps && {
                    ...context.containerExtraProps,
                })}
                enable={enable}
            >
                <MotionableBox
                    motionable={!!context.motionSettings.gesture}
                    motionGesture={context.motionSettings.gesture}
                    motionEffects={context.motionSettings.effects}
                    motionStaggeringChildren={
                        context.motionSettings.motionStaggeringChildren
                    }
                >
                    <WrappedShopUI
                        {...props}
                        enable={enable}
                        ref={ref}
                        style={{
                            ...context.style,
                            ...((type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL ||
                                type === ELEMENT_TYPE.GRID_SECONDARY_LEVEL ||
                                type === ELEMENT_TYPE.GRID_SECONDARY_LEVEL_INNER) &&
                                context.containerStyle),
                        }}
                        {...omit(context, ['style', 'containerStyle'])}
                        isEditingMode={false}
                        isBuyer
                    />
                </MotionableBox>
            </Box>
        );
    }

    return UINode;
}
